import { Close, View, WarningAltFilled } from '@carbon/icons-react';
import * as React from 'react';
import {
  Body,
  Button,
  Heading,
  Icon,
  IconComponentType,
  Tooltip,
} from 'vet-bones/design-system/components';

import {
  TEST_IDS,
  getProjectResourceCardRemoveButtonTestId,
  getProjectResourceCardViewPreviewButtonTestId,
} from 'src/constants/testIds';
import * as copy from 'src/templates/copy';
import { FileCreationMetadata } from 'src/types/fileUpload';

import styles from 'src/containers/ProjectNewResourceDialog/components/projectResourceCard.module.scss';

export type ProjectResourceCardProps = {
  resourceName: string;
  resourceIcon: IconComponentType;
  fileId: string;
  index: number;
  metadata?: FileCreationMetadata;
  validationError?: string;
  onViewPreview: (index: number) => void;
  onRemove: (index: number) => void;
};

export const ProjectResourceCard: React.VFC<ProjectResourceCardProps> = ({
  resourceName,
  resourceIcon,
  fileId,
  index,
  metadata,
  validationError,
  onViewPreview,
  onRemove,
}) => {
  const description = metadata
    ? copy.components.projectResourceDialog.resourceTypePanel.projectResourceCard.description(
        metadata.fields.length,
        metadata.rowCount,
        metadata.fields.map((f) => f.label).join(', ')
      )
    : null;

  return (
    <div className={styles.projectResourceCard}>
      <div className={styles.header}>
        <div className={styles.icon}>
          <Icon IconComponent={resourceIcon} size="md" />
        </div>
        <div className={styles.content}>
          <Heading size="sm">{resourceName}</Heading>
        </div>
        <div className={styles.actions}>
          {validationError ? (
            <Tooltip description={validationError}>
              <div
                className={styles.errorIcon}
                data-testid={TEST_IDS.projectResourceDialog.errorIcon}
              >
                <Icon IconComponent={WarningAltFilled} size="md" />
              </div>
            </Tooltip>
          ) : null}
          <Tooltip
            description={
              copy.components.projectResourceDialog.resourceTypePanel
                .projectResourceCard.viewPreview
            }
          >
            <Button
              icon={View}
              onClick={() => onViewPreview(index)}
              size="xs"
              testId={getProjectResourceCardViewPreviewButtonTestId(
                fileId,
                index
              )}
              variant="secondary-transparent-gray"
            />
          </Tooltip>
          <Tooltip
            description={
              copy.components.projectResourceDialog.resourceTypePanel
                .projectResourceCard.removeFile
            }
          >
            <Button
              icon={Close}
              onClick={() => onRemove(index)}
              size="xs"
              testId={getProjectResourceCardRemoveButtonTestId(fileId, index)}
              variant="secondary-transparent-gray"
            />
          </Tooltip>
        </div>
      </div>
      {description ? (
        <Tooltip description={description}>
          <Body className={styles.description} size="md">
            {description}
          </Body>
        </Tooltip>
      ) : null}
    </div>
  );
};
